import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, ArrowLeft, FileText, CheckCircle2, XCircle,
  Clock, AlertTriangle, Loader2, Users, RefreshCw, Filter,
  ChevronDown, ChevronUp, Mail, Send, Download, AlertCircle, X, UserCheck, Trash2
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { parseBlobError } from "../utils/parseBlobError";
import axios from "axios";
import { fmtUK, fmtUKShort } from "../lib/dateUtils";

import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import { obSendWithheldReason } from "../utils/obSendGuard";
import { clientDisplayName } from "../utils/clientDisplayName";

const STATUS_CONFIG = {
  not_sent:   { label: "Not Sent",   color: "bg-slate-800/50 text-slate-300 border-slate-600/50", icon: Clock },
  sent:       { label: "Sent",       color: "bg-blue-900/30 text-blue-400 border-blue-500/30", icon: Clock },
  partial:    { label: "Partial",    color: "bg-status-amber-dim/30 text-status-amber border-amber-500/30", icon: AlertCircle },
  complete:   { label: "Complete",   color: "bg-teal-dim/30 text-teal border-teal/30", icon: CheckCircle2 },
  overridden: { label: "Overridden", color: "bg-purple-900/30 text-purple-400 border-purple-500/30", icon: UserCheck },
  "Not started": { label: "Not Started", color: "bg-slate-800/50 text-slate-300 border-slate-600/50", icon: Clock },
};

const CHECKPOINT_BADGE = {
  "Cleared":            "bg-teal-dim/30 text-teal border-teal/30",
  "Complete":           "bg-teal-dim/30 text-teal border-teal/30",
  "Overridden":         "bg-purple-900/30 text-purple-400 border-purple-500/30",
  "Submitted":          "bg-teal-dim/30 text-teal border-teal/30",
  "Reviewed":           "bg-teal-dim/30 text-teal border-teal/30",
  "Prepared":           "bg-teal-dim/30 text-teal border-teal/30",
  "In progress":        "bg-blue-900/30 text-blue-400 border-blue-500/30",
  "Sent":               "bg-blue-900/30 text-blue-400 border-blue-500/30",
  "Awaiting Thirdfort": "bg-blue-900/30 text-blue-400 border-blue-500/30",
  "Not requested":      "bg-slate-800/50 text-slate-300 border-slate-600/50",
  "Send failed":        "bg-status-red-dim/30 text-status-red border-red-500/30",
  "Partial":            "bg-status-amber-dim/30 text-status-amber border-amber-500/30",
  "Draft":              "bg-navy-hover/60 text-blue-300 border-blue-500/30",
  "Not started":        "bg-slate-800/50 text-slate-300 border-slate-600/50",
  "Not prepared":       "bg-slate-800/50 text-slate-300 border-slate-600/50",
  "Do not act":         "bg-status-red-dim/30 text-status-red border-red-500/30",
  "Override":           "bg-status-amber-dim/30 text-status-amber border-amber-500/30",
};

const DELIVERY_LABEL = {
  sent:    { text: "Delivered", cls: "text-teal" },
  success: { text: "Delivered", cls: "text-teal" },
  error:   { text: "Failed",   cls: "text-status-red font-medium" },
  safe_mode: { text: "Safe Mode", cls: "text-status-amber" },
  not_configured: { text: "N/A", cls: "text-slate-500" },
};

const NOT_SENT_STATUSES = ["not_sent", "not sent", "not started"];
const TERMINAL_STATUSES = ["complete", "overridden"];

function isNotSent(raw) {
  if (!raw) return true;
  return NOT_SENT_STATUSES.includes(raw.toLowerCase());
}

function isTerminal(raw) {
  if (!raw) return false;
  return TERMINAL_STATUSES.includes(raw.toLowerCase());
}

function isInFlight(raw) {
  return !isNotSent(raw) && !isTerminal(raw);
}

function CheckpointBadge({ status }) {
  const cls = CHECKPOINT_BADGE[status] || "bg-slate-800/50 text-slate-300 border-slate-600/50";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] rounded border ${cls}`}>
      {status}
    </span>
  );
}

export default function IntakePreopen() {
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [feeEarnerFilter, setFeeEarnerFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [supportMap, setSupportMap] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [resending, setResending] = useState(null);
  const [reminding, setReminding] = useState(null);
  const [isDirector, setIsDirector] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);
  // Brief 14c-i — routing-confirmation banner. Read once on mount from
  // sessionStorage; flag is written by RiskAssessmentPanel on a
  // successful MLCO routing chain. Cleared after the user dismisses it
  // or after 10 minutes (whichever first), so stale flags do not linger.
  const [mlcoRoutedFlag, setMlcoRoutedFlag] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { fetchMatters(); fetchSupportMap(); checkDirectorAccess(); }, []);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("intake_routed_to_mlco");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && parsed.matterRef) {
        const ageMs = Date.now() - (parsed.ts || 0);
        if (ageMs >= 0 && ageMs < 10 * 60 * 1000) {
          setMlcoRoutedFlag(parsed);
        } else {
          sessionStorage.removeItem("intake_routed_to_mlco");
        }
      }
    } catch (err) {
      // nosilent: sessionStorage parse — banner is best-effort visual aid
      console.warn("Could not parse mlco routing flag:", err);
    }
  }, []);

  const dismissMlcoBanner = () => {
    setMlcoRoutedFlag(null);
    try {
      sessionStorage.removeItem("intake_routed_to_mlco");
    } catch (err) {
      // nosilent: sessionStorage remove — banner is best-effort visual aid
      console.warn("Could not clear mlco routing flag:", err);
    }
  };

  const checkDirectorAccess = async () => {
    try {
      const userId = sessionStorage.getItem("bonsai_user_id");
      if (!userId) return;
      const resp = await axios.get(`${API}/user-role`, {
        headers: { "X-Bonsai-User-Id": userId }
      });
      setIsDirector(resp.data?.is_director === true);
    } catch { /* non-critical */ }
  };

  const handleDelete = async () => {
    if (!deleteTarget || !deleteReason.trim()) return;
    setDeleting(true);
    try {
      const userId = sessionStorage.getItem("bonsai_user_id");
      await axios.delete(`${API}/submissions/${deleteTarget.id}`, {
        data: { reason: deleteReason.trim() },
        headers: { "X-Bonsai-User-Id": userId }
      });
      toast.success(`Deleted: ${deleteTarget.client_name || deleteTarget.matter_ref || deleteTarget.provisional_id}`);
      setDeleteTarget(null);
      setDeleteReason("");
      setExpandedId(null);
      fetchMatters();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to delete"));
    } finally {
      setDeleting(false);
    }
  };

  const fetchMatters = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/matters/intake-preopen`);
      setMatters(res.data);
    } catch (err) {
      console.error("Failed to fetch intake matters:", err);
      toast.error("Failed to load intake matters");
    } finally {
      setLoading(false);
    }
  };

  const fetchSupportMap = async () => {
    try {
      const res = await axios.get(`${API}/lookup/fee-earner-support-map`);
      setSupportMap(res.data || {});
    } catch { /* non-critical */ }
  };

  const handleResend = async (refId) => {
    setResending(refId);
    try {
      const res = await axios.post(`${API}/onboarding/resend-ob/${refId}`);
      if (res.data.email_error) {
        toast.error(`Delivery issue: ${res.data.email_error}`);
      } else {
        toast.success(`Onboarding invite resent for ${refId}`);
      }
      fetchMatters();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to resend"));
    } finally {
      setResending(null);
    }
  };

  const handleRemind = async (refId) => {
    setReminding(refId);
    try {
      const res = await axios.post(`${API}/onboarding/remind-ob/${refId}`);
      if (res.data.email_error) {
        toast.error(`Delivery issue: ${res.data.email_error}`);
      } else {
        toast.success(`Reminder sent for ${refId}`);
      }
      fetchMatters();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to send reminder"));
    } finally {
      setReminding(null);
    }
  };

  const handleDownloadAudit = async (refId) => {
    try {
      const response = await axios.get(`${API}/onboarding/documents/${refId}`, { params: { type: "audit_log" }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Bonsai_Law_audit_log_${refId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Audit Log downloaded");
    } catch (err) {
      const detail = await parseBlobError(err);
      toast.error(`Failed to download audit log: ${detail}`);
    }
  };

  const deliveryLabel = (d, rec) => {
    const cfg = DELIVERY_LABEL[d];
    if (cfg) return <span className={cfg.cls}>{cfg.text}</span>;
    if (rec?.email_sent_at) {
      return <span className="text-blue-400 text-[10px]" title="Email was sent but delivery tracking was not available">Sent</span>;
    }
    if (!d || d === "unknown" || d === "none") {
      return <span className="text-slate-500 italic text-[10px]" title="Record created before email tracking">Historical</span>;
    }
    return <span className="text-slate-500">-</span>;
  };

  const feeEarners = useMemo(() => {
    const set = new Set(matters.map(m => m.fee_earner).filter(Boolean));
    ["Emily Howard", "Himanie Dubois", "Rebekah Brake Manning", "Sangita Gohil", "Vanessa Challess"].forEach(name => set.add(name));
    return [...set].sort();
  }, [matters]);

  const filtered = useMemo(() => {
    let list = [...matters];
    if (feeEarnerFilter !== "all") {
      const allowed = new Set([feeEarnerFilter, ...(supportMap[feeEarnerFilter] || [])]);
      list = list.filter(m => allowed.has(m.fee_earner));
    }
    if (statusFilter !== "all") {
      list = list.filter(m => m.ob_status_raw === statusFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        clientDisplayName(m).toLowerCase().includes(q) ||
        (m.matter_ref || "").toLowerCase().includes(q) ||
        (m.provisional_id || "").toLowerCase().includes(q) ||
        (m.fee_earner || "").toLowerCase().includes(q) ||
        (m.primary_client?.email || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [matters, feeEarnerFilter, statusFilter, search, supportMap]);

  const hasActiveFilters = statusFilter !== "all" || feeEarnerFilter !== "all" || search;

  const IN_PROGRESS_STATUSES = ["sent", "partial", "not_sent", "Sent", "Partial", "Not Sent"];
  const COMPLETE_STATUSES = ["complete", "overridden"];

  const stats = useMemo(() => ({
    total: matters.length,
    inProgress: matters.filter(m => IN_PROGRESS_STATUSES.includes(m.ob_status_raw || m.ob_status)).length,
    complete: matters.filter(m => COMPLETE_STATUSES.includes(m.ob_status_raw)).length,
    forfReady: matters.filter(m => m.draft_forf_id).length,
  }), [matters]);

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-hover flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-hover" data-testid="intake-preopen-page">

      {mlcoRoutedFlag && (
        <div
          className="bg-status-amber-dim/30 border-b border-amber-500/40 text-status-amber"
          data-testid="mlco-routing-banner"
        >
          <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-semibold">
                Matter routed to MLCO for review
                {mlcoRoutedFlag.matterRef ? ` (${mlcoRoutedFlag.matterRef})` : ""}
              </p>
              <p className="text-xs mt-1 opacity-80">
                You will be notified when MLCO completes their review.
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={dismissMlcoBanner}
              className="h-7 text-xs text-status-amber hover:bg-amber-500/10"
              data-testid="mlco-routing-banner-dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="glass-nav">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white" data-testid="page-title">Intake / Pre-open</h1>
              <p className="text-sm text-slate-500">File opening in progress — matters not yet open under the Golden Rule</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-navy-hover border border-navy-border rounded-md px-3 py-3 text-center">
              <p className="text-2xl font-bold text-white">{stats.total}</p>
              <p className="text-[11px] text-txt-muted">Total Matters</p>
            </div>
            <div className="bg-navy-hover border border-amber-500/40 rounded-md px-3 py-3 text-center">
              <p className="text-2xl font-bold text-status-amber">{stats.inProgress}</p>
              <p className="text-[11px] text-status-amber/70">Awaiting Completion</p>
            </div>
            <div className="bg-navy-hover border border-teal/40 rounded-md px-3 py-3 text-center">
              <p className="text-2xl font-bold text-teal">{stats.complete}</p>
              <p className="text-[11px] text-teal/70">OB Complete</p>
            </div>
            <div className="bg-navy-hover border border-teal/50 rounded-md px-3 py-3 text-center">
              <p className="text-2xl font-bold text-teal">{stats.forfReady}</p>
              <p className="text-[11px] text-teal/70">Ready for FORF</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between">
            <div className="flex flex-wrap gap-2 flex-1">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36 h-9 text-xs border-navy-border bg-navy-hover text-txt-muted" data-testid="intake-status-filter">
                  <Filter className="w-3 h-3 mr-1" /><SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="not_sent">Not Sent</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                  <SelectItem value="overridden">Overridden</SelectItem>
                </SelectContent>
              </Select>
              <Select value={feeEarnerFilter} onValueChange={setFeeEarnerFilter}>
                <SelectTrigger className="w-44 h-9 text-xs border-navy-border bg-navy-hover text-txt-muted" data-testid="fee-earner-filter">
                  <Users className="w-3 h-3 mr-1" /><SelectValue placeholder="All Fee Earners" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Fee Earners</SelectItem>
                  {feeEarners.map(fe => (
                    <SelectItem key={fe} value={fe}>
                      {fe}{supportMap[fe] ? ` (+ ${supportMap[fe].join(", ")})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative w-56">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-txt-muted" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search name, ref, email..."
                  className="pl-8 h-9 text-xs border-navy-border bg-navy-hover text-white placeholder-[#7a86a8]"
                  data-testid="search-input"
                />
              </div>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={() => { setStatusFilter("all"); setFeeEarnerFilter("all"); setSearch(""); }} className="h-9 text-xs text-txt-muted hover:text-white" data-testid="clear-filters-btn">
                  <X className="w-3 h-3 mr-1" /> Clear
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">{filtered.length} matter{filtered.length !== 1 ? "s" : ""}</span>
              <Button variant="outline" size="sm" onClick={fetchMatters} className="h-9 gap-1.5 text-xs border-navy-border text-txt-muted hover:bg-navy-hover hover:text-white" data-testid="refresh-btn">
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="max-w-[1600px] mx-auto px-6 py-4">
        <div className="border border-navy-border rounded-md overflow-hidden bg-navy-hover" data-testid="intake-table">
          {/* Header row */}
          <div className="hidden lg:grid lg:grid-cols-[2fr_90px_90px_70px_70px_70px_70px_70px_70px_140px_60px] gap-1 px-4 py-2.5 bg-teal/20 border-b border-teal/30 text-[10px] font-bold uppercase tracking-wider text-teal">
            <div>Client</div>
            <div>Date</div>
            <div>OB Status</div>
            <div>Delivery</div>
            <div>Conflict</div>
            <div>AML</div>
            <div>FORF</div>
            <div>CCL</div>
            <div>Care Pack</div>
            <div>Actions</div>
            <div className="text-right">Audit</div>
          </div>

          {/* Rows */}
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-10 h-10 text-txt-faint mx-auto mb-3" />
              <p className="text-txt-muted text-sm">
                {search || hasActiveFilters ? "No matters match your filters" : "No matters currently in file-opening pipeline"}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-navy-border/50">
              {filtered.map(m => {
                const expanded = expandedId === m.id;
                const obCfg = STATUS_CONFIG[m.ob_status_raw] || STATUS_CONFIG["Not started"];
                const ObIcon = obCfg.icon;
                const refId = m.matter_ref || m.provisional_id;
                const obWithheld = obSendWithheldReason({
                  ...m,
                  status: m.ob_status_raw,
                });

                return (
                  <div key={m.id} className={`${expanded ? "bg-navy-hover" : "bg-navy-hover hover:bg-navy-hover"} transition-colors`} data-testid={`intake-row-${m.id}`}>
                    {/* Main row */}
                    <div
                      role="button"
                      tabIndex={0}
                      className="w-full text-left px-4 py-3 lg:grid lg:grid-cols-[2fr_90px_90px_70px_70px_70px_70px_70px_70px_140px_60px] lg:gap-1 lg:items-center flex flex-wrap items-center gap-2 cursor-pointer"
                      onClick={() => setExpandedId(expanded ? null : m.id)}
                      onKeyDown={(e) => { if (e.key === "Enter") setExpandedId(expanded ? null : m.id); }}
                    >
                      {/* Client */}
                      <div className="min-w-0 flex-1 lg:flex-none">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-sm text-white truncate">{clientDisplayName(m) || "\u2014"}</span>
                          <span className="text-[10px] text-txt-muted font-mono shrink-0">{m.matter_ref || m.provisional_id || ""}</span>
                          {m.draft_forf_id && <FileText className="w-3 h-3 text-teal shrink-0" title="FORF Ready" />}
                        </div>
                        <div className="text-[10px] text-txt-muted">{m.fee_earner || "\u2014"}</div>
                      </div>

                      {/* Date */}
                      <div className="text-[11px] text-txt-muted hidden lg:block">{fmtUKShort(m.created_at)}</div>

                      {/* OB Status */}
                      <div>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border ${obCfg.color}`}>
                          <ObIcon className="w-3 h-3" /> {obCfg.label}
                        </span>
                      </div>

                      {/* Delivery */}
                      <div className="text-[10px]">
                        {isNotSent(m.ob_status_raw)
                          ? <span className="text-slate-500">-</span>
                          : deliveryLabel(m.email_delivery, m)
                        }
                      </div>

                      {/* Checkpoints */}
                      <div className="hidden lg:block"><CheckpointBadge status={m.conflict_status} /></div>
                      <div className="hidden lg:block"><CheckpointBadge status={m.thirdfort_status} /></div>
                      <div className="hidden lg:block"><CheckpointBadge status={m.forf_status} /></div>
                      <div className="hidden lg:block"><CheckpointBadge status={m.ccl_status} /></div>
                      <div className="hidden lg:block"><CheckpointBadge status={m.care_pack_status} /></div>

                      {/* Actions */}
                      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                        {/* BM-H278 (Vanessa, 4 Sep 2026): this list treated
                            only complete and overridden as terminal, so a
                            skipped or waived_by_link matter fell into
                            isInFlight and was offered Resend OB and Remind.
                            The backend now ships the answer on the row. */}
                        {obWithheld && (
                          <span className="text-[10px] text-teal" data-testid={`ob-satisfied-${refId}`}>
                            {obWithheld}
                          </span>
                        )}
                        {!obWithheld && isNotSent(m.ob_status_raw) && (
                          <Button
                            variant="ghost" size="sm"
                            className="h-6 px-1.5 text-[10px] text-status-amber hover:text-status-amber hover:bg-status-amber-dim/20"
                            onClick={() => handleResend(refId)}
                            disabled={resending === refId}
                            data-testid={`send-ob-${refId}`}
                            title="Send initial onboarding invitation"
                          >
                            {resending === refId ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                            <span className="ml-0.5">Send OB</span>
                          </Button>
                        )}
                        {!obWithheld && isInFlight(m.ob_status_raw) && (
                          <>
                            <Button
                              variant="ghost" size="sm"
                              className="h-6 px-1.5 text-[10px] text-teal hover:text-teal hover:bg-teal-dim/20"
                              onClick={() => handleResend(refId)}
                              disabled={resending === refId}
                              data-testid={`resend-full-${refId}`}
                              title="Resend onboarding invitation link"
                            >
                              {resending === refId ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                              <span className="ml-0.5">Resend OB</span>
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              className="h-6 px-1.5 text-[10px] text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                              onClick={() => handleRemind(refId)}
                              disabled={reminding === refId}
                              data-testid={`remind-${refId}`}
                              title="Send reminder"
                            >
                              {reminding === refId ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                              <span className="ml-0.5">Remind</span>
                            </Button>
                          </>
                        )}
                      </div>

                      {/* Audit */}
                      <div className="flex items-center gap-1 justify-end" onClick={e => e.stopPropagation()}>
                        {refId && (
                          <Button
                            variant="ghost" size="sm"
                            className="h-6 px-1.5 text-[10px] text-txt-muted hover:text-white hover:bg-navy-hover"
                            onClick={() => handleDownloadAudit(refId)}
                            data-testid={`download-audit-${refId}`}
                            title="Download Audit Log"
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                        )}
                        {expanded ? <ChevronUp className="w-4 h-4 text-txt-muted" /> : <ChevronDown className="w-4 h-4 text-txt-muted" />}
                      </div>
                    </div>

                    {/* Expanded detail */}
                    {expanded && (
                      <div className="border-t border-navy-border bg-navy/80 px-4 py-4 space-y-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                          <Detail label="Client Name" value={clientDisplayName(m)} />
                          <Detail label="Email" value={m.primary_client?.email} />
                          <Detail label="Phone" value={m.primary_client?.phone} />
                          <Detail label="Entity" value={m.entity_type} />
                          <Detail label="Fee Earner" value={m.fee_earner} />
                          <Detail label="Matter Reference" value={m.matter_ref || m.provisional_id} />
                          <Detail label="Created" value={fmtUK(m.created_at)} />
                        </div>
                        <div className="border-t border-navy-border pt-3">
                          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 text-xs">
                            <div><span className="text-txt-muted block mb-1">Conflict</span><CheckpointBadge status={m.conflict_status} /></div>
                            <div><span className="text-txt-muted block mb-1">AML/Thirdfort</span><CheckpointBadge status={m.thirdfort_status} /></div>
                            <div><span className="text-txt-muted block mb-1">Onboarding</span><CheckpointBadge status={m.ob_status} /></div>
                            <div><span className="text-txt-muted block mb-1">FORF</span><CheckpointBadge status={m.forf_status} /></div>
                            <div><span className="text-txt-muted block mb-1">CCL</span><CheckpointBadge status={m.ccl_status} /></div>
                            <div><span className="text-txt-muted block mb-1">Care Pack</span><CheckpointBadge status={m.care_pack_status} /></div>
                          </div>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button size="sm" variant="outline" className="text-xs border-navy-border text-txt-muted hover:bg-navy-hover" onClick={() => navigate(`/submission/${m.id}`)} data-testid={`view-submission-${m.id}`}>
                            <FileText className="w-3 h-3 mr-1" /> View Submission
                          </Button>
                          {isDirector && (
                            <Button size="sm" variant="outline" className="text-xs border-red-500/40 text-status-red hover:bg-status-red-dim/20 hover:text-status-red" onClick={() => { setDeleteTarget(m); setDeleteReason(""); }} data-testid={`delete-intake-${m.id}`}>
                              <Trash2 className="w-3 h-3 mr-1" /> Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="delete-intake-modal">
          <div className="bg-navy-hover rounded-md shadow-2xl p-6 max-w-md w-full mx-4 border border-red-500/40">
            <h3 className="text-lg font-semibold text-status-red mb-3">Delete Intake Entry</h3>
            <p className="text-sm text-txt-muted mb-1">
              Permanently delete <strong className="text-white">{clientDisplayName(deleteTarget) || "Unknown"}</strong>
              {deleteTarget.matter_ref && <span className="text-txt-muted ml-1">({deleteTarget.matter_ref})</span>}
              {!deleteTarget.matter_ref && deleteTarget.provisional_id && <span className="text-txt-muted ml-1">({deleteTarget.matter_ref || deleteTarget.provisional_id})</span>}
            </p>
            <p className="text-xs text-txt-muted mb-2">This will also remove linked conflict checks, onboarding records, checklists, and completions book entries.</p>
            <p className="text-xs text-status-red font-medium mb-3">This action cannot be undone.</p>
            <textarea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Reason for deletion (required)..."
              rows={3}
              className="w-full border border-navy-border rounded-md px-3 py-2 text-sm bg-navy text-white placeholder-[#7a86a8] focus:outline-none focus:ring-2 focus:ring-red-500/40 mb-4 resize-none"
              data-testid="delete-intake-reason-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)} disabled={deleting} className="border-navy-border text-txt-muted hover:bg-navy-hover">Cancel</Button>
              <Button size="sm" onClick={handleDelete} disabled={deleting || !deleteReason.trim()} className="bg-status-red hover:bg-status-red text-white" data-testid="confirm-delete-intake-btn">
                {deleting ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Deleting...</> : "Delete Permanently"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value, color }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-txt-muted mb-0.5">{label}</p>
      <p className={`text-sm ${color || "text-txt"}`}>{value || "\u2014"}</p>
    </div>
  );
}
